import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, ChangeDetectorRef, HostListener, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, take } from 'rxjs';
import { TripPlannerComponent } from './trip-planner.component';
import { TripBookingService, TripStop } from './trip-booking.service';
import { RoomCardComponent } from '../partner/components/room-card.component';
import { WhenComponent } from '../when/when.component';
import { LocaleService } from '../locale.service';
import { TripIdeasComponent, TripIdea, TRIP_IDEAS } from './trip-ideas.component';
import { validStay } from '../partner/booking-search';
@Component({
  selector: 'app-home-trip-planner', standalone: true,
  imports: [CommonModule, FormsModule, RoomCardComponent, WhenComponent, TripIdeasComponent],
  providers: [TripBookingService], encapsulation: ViewEncapsulation.None,
  templateUrl: './home-trip-planner.component.html', styleUrls: ['./home-trip-planner.component.scss'],
})
export class HomeTripPlannerComponent extends TripPlannerComponent {
  @Input() initialSearch: any;
  @Output() exit = new EventEmitter<void>();
  dates: Record<string, string[]> = {};
  focusedDates = '';
  activeStopKey = '';
  resultsVisible = false;
  narrow = false;
  homeReady = false;
  hostels: Record<string, any[]> = {};
  hostelLoading: Record<string, boolean> = {};
  hostelErrors: Record<string, string> = {};
  hostelFilters: Record<string, string> = {};
  private cityVersions: Record<string, number> = {};
  private hostelVersions: Record<string, number> = {};
  private cityTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  private homeAlive = true;
  override ngOnDestroy() { this.homeAlive=false; Object.values(this.cityTimers).forEach(clearTimeout); super.ngOnDestroy(); }
  private updateHome() { if(this.homeAlive)this.homeCdr.detectChanges(); }

  constructor(api: TripBookingService, router: Router, private homeRoute: ActivatedRoute, private homeCdr: ChangeDetectorRef, private locale: LocaleService) {
    super(api, router, homeRoute, homeCdr); this.resize();
  }
  @HostListener('window:resize') resize() { this.narrow = typeof window !== 'undefined' && window.innerWidth < 959; }
  override async ngOnInit() {
    this.homeReady=false;this.error=''; this.loading=true;
    await super.ngOnInit();
    if(!this.homeAlive)return;
    for(const stop of this.draft.stays) {
      if(stop.property && !stop.city)stop.city={city:stop.property.city,country:stop.property.country};
      this.searches[stop.key]=stop.city?.city || '';
    }
    if (this.error) return;
    const seed = this.initialSearch;
    if(seed?.routeId){const idea=TRIP_IDEAS.find(i=>i.id===seed.routeId);if(idea)this.useRoute(idea);}
    const currency = seed?.currency || this.homeRoute.snapshot.queryParamMap.get('currency') || (await firstValueFrom(this.locale.currentCurrency.pipe(take(1))))?.code;
    if (this.config.currencies.includes(currency) && this.draft.currency!==currency) { this.draft.currency=currency; for(const stop of this.draft.stays) if(stop.property) await this.loadRooms(stop,true); }
    if (seed) {
      if (Number.isInteger(Number(seed.who)) && seed.who >= 1 && seed.who <= 20 && this.draft.guests!==Number(seed.who)) { this.draft.guests = Number(seed.who); await this.detailsChanged(); }
      const first = this.draft.stays[0];
      if (seed.when?.length === 2 && validStay(seed.when[0], seed.when[1])) {
        first.checkIn = seed.when[0]; first.checkOut = seed.when[1];
        if (!this.draft.stays[1].property) Object.assign(this.draft.stays[1], {checkIn: first.checkOut, checkOut: this.api.stop(first.checkOut).checkOut});
      }
      if (seed.where?.searchType === 2 && /^[a-f\d]{24}$/i.test(seed.where.id)) {
        try { await this.choose(first, await this.api.property(seed.where.id)); } catch { this.roomErrors[first.key] = 'This hostel could not be loaded. Please choose another.'; }
      } else if (seed.where) {
        const city=seed.where.city || seed.where.cityName;
        if(city)await this.chooseCity(first,{city,country:seed.where.country || ''});
      }
      this.changed();
    }
    this.draft.stays.forEach(s => this.dates[s.key] = [s.checkIn, s.checkOut]);
    this.activeStopKey = this.draft.stays[0].key;
    this.resultsVisible = this.draft.stays.some(s=>s.city);
    for(const stop of this.draft.stays)if(stop.city && !stop.property)void this.loadHostels(stop);
    const selectedCurrency=this.locale.currencies.find(c=>c.code===this.draft.currency);
    if(selectedCurrency && this.api.browser)this.locale.setCurrencyInPlace(selectedCurrency);
    this.homeReady=true;this.updateHome();
  }
  useRoute(idea:TripIdea) {
    const start=this.draft.stays[0]?.checkIn;
    this.draft.stays=idea.stops.map((city,index)=>{
      const stop=this.api.stop(start);
      stop.city={city:city.city,country:city.country};return stop;
    });
    let checkIn=start || this.api.stop().checkIn;
    idea.stops.forEach((city,index)=>{
      const stop=this.draft.stays[index];stop.checkIn=checkIn;
      stop.checkOut=new Date(Date.parse(checkIn+'T00:00:00Z')+city.nights*86400000).toISOString().slice(0,10);
      checkIn=stop.checkOut;this.dates[stop.key]=[stop.checkIn,stop.checkOut];this.searches[stop.key]=city.city;
    });
    this.active='';this.activeStopKey=this.draft.stays[0].key;this.resultsVisible=true;this.changed();
    for(const stop of this.draft.stays)void this.loadHostels(stop);
  }
  async changeCurrency() {
    const currency=this.locale.currencies.find(c=>c.code===this.draft.currency);
    if(currency)this.locale.setCurrencyInPlace(currency);
    for(const stop of this.draft.stays)if(stop.property)await this.loadRooms(stop,true);
    this.changed();
    for(const stop of this.draft.stays)if(stop.city)void this.loadHostels(stop);
  }
  get guestError() { return Number.isInteger(Number(this.draft.guests)) && this.draft.guests>=1 && this.draft.guests<=20 ? '' : 'Choose between 1 and 20 guests for your trip.'; }
  changeGuests(delta: number) { this.draft.guests=Math.max(1,Math.min(20,Number(this.draft.guests)+delta)); void this.detailsChanged(); }
  searchChanged(stop: TripStop, value: string) {
    if(stop.city || stop.property) {
      stop.city=undefined; stop.property=null; stop.selected=[]; this.rooms[stop.key]=[];
      this.hostels[stop.key]=[]; this.hostelVersions[stop.key]=(this.hostelVersions[stop.key]||0)+1; this.changed();
    }
    this.searches[stop.key]=value;
    // Invalidate an in-flight response immediately, before the debounce expires.
    this.cityVersions[stop.key]=(this.cityVersions[stop.key]||0)+1;
    this.results[stop.key]=[]; clearTimeout(this.cityTimers[stop.key]);
    this.cityTimers[stop.key]=setTimeout(()=>void this.search(stop),250);
  }
  override async search(stop: TripStop) {
    this.active=stop.key;
    const version=(this.cityVersions[stop.key]||0)+1; this.cityVersions[stop.key]=version;
    const query=this.searches[stop.key] || '';
    this.results[stop.key]=[]; this.searching[stop.key]=false;
    if(query.trim().length<2)return;
    this.searching[stop.key]=true;
    this.hostelErrors[stop.key]='';
    try {
      const cities=await this.api.destinations(query);
      if(this.cityVersions[stop.key]===version)this.results[stop.key]=cities;
    } catch(e) { if(this.cityVersions[stop.key]===version)this.hostelErrors[stop.key]=this.message(e,'City search is unavailable. Please try again.'); }
    finally { if(this.cityVersions[stop.key]===version)this.searching[stop.key]=false; this.updateHome(); }
  }
  async chooseCity(stop: TripStop, city: {city:string;country:string}) {
    this.cityVersions[stop.key]=(this.cityVersions[stop.key]||0)+1;
    this.hostelFilters[stop.key]='';stop.city=city;stop.property=null;stop.selected=[];this.rooms[stop.key]=[];
    this.searches[stop.key]=city.city;this.active='';this.activeStopKey=stop.key;this.resultsVisible=true;this.changed();
    await this.loadHostels(stop);
  }
  async loadHostels(stop: TripStop) {
    const key=stop.key,version=(this.hostelVersions[key]||0)+1;this.hostelVersions[key]=version;
    this.hostels[key]=[];this.hostelErrors[key]='';this.hostelLoading[key]=false;
    if(!stop.city || this.dateError || this.guestError)return;
    this.hostelLoading[key]=true;
    try { const data=await this.api.availableHostels(stop,this.draft);if(this.hostelVersions[key]===version)this.hostels[key]=data; }
    catch(e) { if(this.hostelVersions[key]===version)this.hostelErrors[key]=this.message(e,'We could not check available hostels. Please try again.'); }
    finally { if(this.hostelVersions[key]===version)this.hostelLoading[key]=false;this.updateHome(); }
  }
  availableFor(stop: TripStop) { const q=(this.hostelFilters[stop.key]||'').trim().toLowerCase();return (this.hostels[stop.key]||[]).filter(p=>!q || p.name.toLowerCase().includes(q)); }
  override async choose(stop: TripStop, property: any) {
    stop.city={city:property.city,country:property.country};
    await super.choose(stop,property);this.searches[stop.key]=stop.city.city;this.resultsVisible=true;this.activeStopKey=stop.key;this.changed();this.updateHome();
  }
  override changeProperty(stop: TripStop) {
    stop.property=null;stop.selected=[];this.rooms[stop.key]=[];this.changed();void this.loadHostels(stop);
  }
  override async datesChanged(stop: TripStop) { await super.datesChanged(stop); if(stop.city)await this.loadHostels(stop); }
  override async detailsChanged() { await super.detailsChanged(); for(const stop of this.draft.stays)if(stop.city)await this.loadHostels(stop); }
  get currentStop() { return this.draft.stays.find(s=>s.key===this.activeStopKey) || this.draft.stays[0]; }
  openCalendar(calendar: WhenComponent, key: string, event: Event) {
    if(!this.homeReady)return;
    if ((event.target as HTMLElement)?.closest?.('.calendar-wrapper')) return;
    this.active=''; this.focusedDates=key; calendar.setFocus(true);
  }
  setDates(stop: TripStop, value: string[]) {
    if (!value || value.length !== 2) return;
    this.dates[stop.key] = value; stop.checkIn = value[0]; stop.checkOut = value[1]; setTimeout(()=>this.focusedDates='',10);
    const next = this.draft.stays[this.draft.stays.indexOf(stop)+1];
    if (next && !next.property && next.checkIn < stop.checkOut) {
      const end = this.api.stop(stop.checkOut); next.checkIn = end.checkIn; next.checkOut = end.checkOut; this.dates[next.key] = [next.checkIn,next.checkOut]; if(next.city)void this.loadHostels(next);
    }
    void this.datesChanged(stop);
  }
  override addStop() { super.addStop(); const s=this.draft.stays.at(-1)!; this.dates[s.key]=[s.checkIn,s.checkOut]; }
  override remove(stop: TripStop) { super.remove(stop); if(this.activeStopKey===stop.key)this.activeStopKey=this.draft.stays[0].key; }
  searchTrip() {
    this.active = ''; this.focusedDates = ''; this.resultsVisible = true;
    for(const stop of this.draft.stays)if(stop.city && !stop.property)void this.loadHostels(stop);
    this.activeStopKey = (this.draft.stays.find(s=>s.property && this.selected(s)!==Number(this.draft.guests)) || this.draft.stays[0]).key;
    setTimeout(()=>document.getElementById('home-trip-results')?.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth',block:'start'}),0);
  }
  nextStop() {
    const index=this.draft.stays.indexOf(this.currentStop); const next=this.draft.stays[index+1];
    if(next)this.activeStopKey=next.key;
  }
  title(stop: TripStop) { return stop.city?.city || stop.property?.city || 'Choose a city'; }
}
